import type { PropsWithChildren, ReactNode } from 'react';

import { BrandLockup } from './brand-lockup';

/**
 * The one layout for every screen before a user has work of their own —
 * sign-in, the magic-link landing, onboarding, and the empty home page: the
 * board's dotted surface, the Floto Rooms lockup top-left, an optional
 * top-right slot (the account controls once signed in), and a single centered
 * column for the screen's one job.
 *
 * Shared so those screens read as one continuous flow rather than four
 * designs.
 */
export function CenteredShell({
  children,
  top,
  width = 'max-w-xl',
}: PropsWithChildren<{
  top?: ReactNode;
  /** The column's width — the onboarding form wants less than a link field. */
  width?: string;
}>) {
  return (
    <main className="relative isolate flex min-h-svh flex-col overflow-hidden bg-surface">
      {/* The board's own dotted surface. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.07] [background-image:radial-gradient(#1a1a1a_1px,transparent_1px)] [background-size:24px_24px]"
      />

      <header className="flex h-[76px] items-center justify-between px-6 lg:px-10">
        <BrandLockup />
        <div className="flex items-center gap-3">{top}</div>
      </header>

      <section
        className={`relative z-10 mx-auto flex w-full flex-1 flex-col items-center justify-center px-6 pt-6 pb-24 text-center ${width}`}
      >
        {children}
      </section>
    </main>
  );
}

/** The screen's headline and the line under it, in the shell's type. */
export function ShellHeading({
  children,
  sub,
}: {
  children: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <>
      <h1 className="!mt-6 !mb-0 !text-section-title !font-bold !tracking-tight !text-text-primary duration-700 animate-in fade-in slide-in-from-bottom-3 motion-reduce:animate-none">
        {children}
      </h1>
      {sub ? (
        <p className="max-w-md pt-3 text-body leading-relaxed text-text-secondary duration-700 animate-in fade-in slide-in-from-bottom-3 motion-reduce:animate-none">
          {sub}
        </p>
      ) : null}
    </>
  );
}
